import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

/**
 * Exchanges the org token before rendering anything beneath it.
 *
 * Letting the subtree mount first means its first queries go out carrying the
 * previous school's token, which reads as an *empty* organisation rather than
 * as an error — a wrong answer instead of a visible failure. For a teacher who
 * works at two schools that is the difference between "my class has no
 * learners" and "hold on, I'm at the wrong school".
 *
 * Mirrors AMBIT's `src/altitude/OrgGate.tsx`, including the cache purge, which
 * is the part that is easy to leave out and impossible to debug afterwards.
 */
export function OrgGate({ children }: { children: (orgSlug: string) => React.ReactNode }) {
  const { orgSlug = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setOrgContext = useSession(state => state.setOrgContext);

  const [ready, setReady] = useState(() => orgSlug === apiClient.getOrgSlug() && apiClient.hasOrgToken());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    apiClient
      .exchangeOrgToken(orgSlug)
      .then(result => {
        if (cancelled) return;
        /*
         * No query key carries the org — `['courses']` means "this session's
         * courses" — so a cache that survives the exchange hands the new school
         * the old one's rows, which reads as real data rather than as the stale
         * answer it is. Purging everything is blunt and correct; the alternative
         * is remembering to namespace every key forever.
         */
        queryClient.clear();
        setOrgContext(result.org, result.permissions, result.entitlements);
        setReady(true);
      })
      .catch((caught: Error) => {
        if (cancelled) return;
        setError(caught.message);
        setReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orgSlug, queryClient, setOrgContext]);

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-lg font-semibold text-ink-900">Cannot open {orgSlug}</p>
        <p className="max-w-md text-sm text-ink-600">{error}</p>
        <button
          type="button"
          className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white"
          onClick={() => navigate('/switch', { replace: true })}
        >
          Choose another organisation
        </button>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-sm text-ink-500">Opening {orgSlug}…</p>
      </div>
    );
  }

  return <>{children(orgSlug)}</>;
}
